import {
  Avatar,
  Badge,
  Box,
  Divider,
  Flex,
  Icon,
  IconButton,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useTarjetero } from 'hooks/useTarjetero';
import { useEpisodesByPatient } from 'hooks/useEpisodes';
import React, { useState } from 'react';
import {
  MdCheck,
  MdContentCopy,
  MdDownload,
  MdEdit,
  MdEmail,
  MdFavoriteBorder,
  MdFolderOpen,
  MdLocationOn,
  MdPhone,
  MdPhoneCallback,
  MdWork,
} from 'react-icons/md';
import { historiaClinicaService } from 'services/historiaClinicaService';
import { generateHistoriaPdf } from 'utils/generateHistoriaPdf';
import {
  ClinicalEpisode,
  EstadoCivil,
  EstadoEpisodio,
  EstadoTarjetero,
  EpisodeSignosVitales,
  Genero,
  Patient,
} from 'types/models';

function calcularEdad(fechaNacimiento: string): number {
  const hoy = new Date();
  const nac = new Date(fechaNacimiento);
  let edad = hoy.getFullYear() - nac.getFullYear();
  if (
    hoy.getMonth() < nac.getMonth() ||
    (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())
  ) {
    edad--;
  }
  return edad;
}

const GENERO_BADGE: Record<Genero, { label: string; colorScheme: string }> = {
  [Genero.MASCULINO]: { label: 'Masculino', colorScheme: 'blue' },
  [Genero.FEMENINO]: { label: 'Femenino', colorScheme: 'pink' },
  [Genero.OTRO]: { label: 'Otro', colorScheme: 'purple' },
};

const AVATAR_GRADIENT: Record<Genero, string> = {
  [Genero.MASCULINO]: 'linear(to-br, blue.400, brand.500)',
  [Genero.FEMENINO]: 'linear(to-br, pink.400, purple.500)',
  [Genero.OTRO]: 'linear(to-br, purple.400, brand.500)',
};

const TARJETERO_DOT: Record<EstadoTarjetero, string> = {
  [EstadoTarjetero.ACTIVO]: '#68D391',
  [EstadoTarjetero.INACTIVO]: '#F6AD55',
  [EstadoTarjetero.ARCHIVADO]: '#A0AEC0',
};

const ESTADO_CIVIL_LABEL: Partial<Record<EstadoCivil, string>> = {
  [EstadoCivil.SOLTERO]: 'Soltero/a',
  [EstadoCivil.CASADO]: 'Casado/a',
  [EstadoCivil.DIVORCIADO]: 'Divorciado/a',
  [EstadoCivil.VIUDO]: 'Viudo/a',
  [EstadoCivil.UNION_LIBRE]: 'Unión Libre',
};

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Tooltip label={copied ? '¡Copiado!' : 'Copiar'} placement='top' hasArrow>
      <IconButton
        aria-label='Copiar'
        icon={<Icon as={copied ? MdCheck : MdContentCopy} w='12px' h='12px' />}
        size='xs'
        variant='ghost'
        colorScheme={copied ? 'green' : 'gray'}
        minW='20px'
        h='20px'
        onClick={handleCopy}
        flexShrink={0}
      />
    </Tooltip>
  );
}

interface InfoRowProps {
  icon: React.ElementType;
  label: string;
  value: string | null | undefined;
  copyable?: boolean;
}

function InfoRow({ icon, label, value, copyable }: InfoRowProps) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const labelColor = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const iconBg = useColorModeValue('brand.50', 'navy.700');

  if (!value) return null;

  return (
    <Flex align='center' gap='2' py='8px'>
      <Flex
        w='26px'
        h='26px'
        bg={iconBg}
        borderRadius='8px'
        align='center'
        justify='center'
        flexShrink={0}>
        <Icon as={icon} color='brand.500' w='13px' h='13px' />
      </Flex>
      <Flex direction='column' minW={0} flex={1}>
        <Text
          fontSize='9px'
          color={labelColor}
          fontWeight='700'
          textTransform='uppercase'
          letterSpacing='0.08em'>
          {label}
        </Text>
        <Text fontSize='xs' color={textColor} fontWeight='600' noOfLines={1}>
          {value}
        </Text>
      </Flex>
      {copyable && <CopyButton value={value} />}
    </Flex>
  );
}

interface PatientCardProps {
  patient: Patient;
  onEdit: () => void;
  [x: string]: any;
}

export default function PatientCard({ patient, onEdit, ...rest }: PatientCardProps) {
  const { data: tarjetero } = useTarjetero(patient.id);
  const episodes: ClinicalEpisode[] = useEpisodesByPatient(patient.id).data ?? [];
  const [pdfLoading, setPdfLoading] = useState(false);
  const toast = useToast();

  const latestSv: EpisodeSignosVitales | null = (() => {
    const active = episodes
      .filter(
        (e) =>
          (e.estado === EstadoEpisodio.ABIERTO ||
            e.estado === EstadoEpisodio.EN_TRATAMIENTO) &&
          e.signosVitales != null,
      )
      .sort(
        (a, b) =>
          new Date(b.fechaApertura).getTime() - new Date(a.fechaApertura).getTime(),
      );
    return active[0]?.signosVitales ?? null;
  })();

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const data = await historiaClinicaService.getResumen(patient.id);
      generateHistoriaPdf(data, `${patient.nombres} ${patient.apellidos}`);
    } catch {
      toast({
        title: 'Error al generar PDF',
        description: 'No se pudo descargar la historia clínica.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const dividerColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const hcBg = useColorModeValue('brand.50', 'navy.700');
  const hcBorder = useColorModeValue('brand.100', 'brand.800');
  const svBg = useColorModeValue('red.50', 'rgba(254,178,178,0.06)');
  const statBg = useColorModeValue('gray.50', 'navy.700');
  const svBorder = useColorModeValue('red.100', 'rgba(254,178,178,0.15)');

  const edad = calcularEdad(patient.fechaNacimiento);
  const { label: generoLabel, colorScheme: generoColor } = GENERO_BADGE[patient.genero];
  const ciudad = [patient.ciudad, patient.provincia].filter(Boolean).join(', ');
  const estadoCivilLabel = patient.estadoCivil
    ? ESTADO_CIVIL_LABEL[patient.estadoCivil]
    : null;

  const svItems = latestSv
    ? [
        latestSv.presionArterial != null && {
          label: 'T/A',
          value: latestSv.presionArterial,
          mono: true,
        },
        latestSv.frecuenciaCardiaca != null && {
          label: 'FC',
          value: `${latestSv.frecuenciaCardiaca} bpm`,
        },
        latestSv.temperatura != null && {
          label: 'T°',
          value: `${latestSv.temperatura}°C`,
        },
        latestSv.frecuenciaRespiratoria != null && {
          label: 'FR',
          value: `${latestSv.frecuenciaRespiratoria} rpm`,
        },
        latestSv.peso != null && {
          label: 'Peso',
          value: `${latestSv.peso} kg`,
        },
        latestSv.talla != null && {
          label: 'Talla',
          value: `${latestSv.talla} m`,
        },
      ].filter(Boolean) as { label: string; value: string; mono?: boolean }[]
    : [];

  return (
    <Card p='24px' {...rest}>
      {/* ── Header: avatar + datos clave + acciones ── */}
      {/* ── Header: avatar + nombre + acciones ── */}
      <Flex align='flex-start' gap='14px' mb='14px'>
        <Box
          bgGradient={AVATAR_GRADIENT[patient.genero]}
          borderRadius='full'
          p='2px'
          flexShrink={0}>
          <Avatar
            size='md'
            name={`${patient.nombres} ${patient.apellidos}`}
            bg='transparent'
            color='white'
            fontSize='md'
            fontWeight='800'
          />
        </Box>

        <Flex direction='column' flex={1} minW={0} gap='5px'>
          <Flex align='center' gap='1'>
            <Text color={textColor} fontSize='md' fontWeight='800' noOfLines={2} flex={1} lineHeight='1.3'>
              {patient.nombres} {patient.apellidos}
            </Text>
            <CopyButton value={`${patient.nombres} ${patient.apellidos}`} />
          </Flex>
          <Flex align='center' gap='6px' flexWrap='wrap'>
            <Badge
              fontFamily='mono'
              fontSize='xs'
              colorScheme='gray'
              borderRadius='full'
              px='2'
              py='0.5'>
              {patient.cedula}
            </Badge>
            <Badge colorScheme={generoColor} borderRadius='full' px='2' py='0.5' fontSize='xs'>
              {generoLabel}
            </Badge>
            <Text fontSize='xs' color={mutedColor} fontWeight='600'>
              {edad} años
            </Text>
            {estadoCivilLabel && (
              <>
                <Text fontSize='xs' color={mutedColor}>·</Text>
                <Text fontSize='xs' color={mutedColor} fontWeight='600'>
                  {estadoCivilLabel}
                </Text>
              </>
            )}
          </Flex>
        </Flex>

        {/* Botones acción */}
        <Flex gap='2px' flexShrink={0}>
          <Tooltip label='Editar datos' placement='top' hasArrow>
            <IconButton
              aria-label='Editar'
              icon={<Icon as={MdEdit} w='15px' h='15px' />}
              size='sm'
              variant='ghost'
              colorScheme='brand'
              onClick={onEdit}
            />
          </Tooltip>
          <Tooltip label='Descargar Historia Clínica PDF' placement='top' hasArrow>
            <IconButton
              aria-label='Descargar PDF'
              icon={<Icon as={MdDownload} w='15px' h='15px' />}
              size='sm'
              variant='ghost'
              colorScheme='gray'
              onClick={handleDownloadPdf}
              isLoading={pdfLoading}
              isDisabled={!tarjetero}
            />
          </Tooltip>
        </Flex>
      </Flex>

      {/* ── Código HC ── */}
      {tarjetero ? (
        <Flex
          align='center'
          gap='6px'
          bg={hcBg}
          border='1px solid'
          borderColor={hcBorder}
          borderRadius='8px'
          px='10px'
          py='6px'
          mb='14px'
          w='fit-content'>
          <Box w='6px' h='6px' borderRadius='full' bg={TARJETERO_DOT[tarjetero.estado]} flexShrink={0} />
          <Icon as={MdFolderOpen} color='brand.500' w='12px' h='12px' flexShrink={0} />
          <Text fontFamily='mono' fontSize='xs' fontWeight='800' color='brand.500'>
            {tarjetero.codigoHc}
          </Text>
          <CopyButton value={tarjetero.codigoHc} />
        </Flex>
      ) : (
        <Flex
          align='center'
          gap='6px'
          bg={statBg}
          borderRadius='8px'
          px='10px'
          py='6px'
          mb='14px'
          w='fit-content'>
          <Icon as={MdFolderOpen} color='secondaryGray.400' w='12px' h='12px' />
          <Text fontSize='xs' color='secondaryGray.400' fontWeight='600'>Sin historia clínica</Text>
        </Flex>
      )}

      <Divider borderColor={dividerColor} mb='14px' />

      {/* ── Información de contacto en 2 columnas ── */}
      <SimpleGrid columns={2} columnGap='12px' rowGap='0'>
        <InfoRow icon={MdPhone} label='Teléfono' value={patient.telefono} copyable />
        <InfoRow icon={MdEmail} label='Email' value={patient.email} copyable />
        <InfoRow
          icon={MdPhoneCallback}
          label='Emergencia'
          value={patient.telefonoEmergencia}
        />
        <InfoRow icon={MdLocationOn} label='Ciudad' value={ciudad || null} />
        <InfoRow icon={MdWork} label='Ocupación' value={patient.ocupacion ?? '—'} />
      </SimpleGrid>

      {/* ── Signos vitales ── */}
      {svItems.length > 0 && (
        <>
          <Divider borderColor={dividerColor} mt='10px' mb='12px' />
          <Box
            bg={svBg}
            border='1px solid'
            borderColor={svBorder}
            borderRadius='10px'
            px='12px'
            py='10px'>
            <Flex align='center' gap='6px' mb='8px'>
              <Icon as={MdFavoriteBorder} color='red.400' w='12px' h='12px' />
              <Text
                fontSize='9px'
                fontWeight='800'
                color='red.400'
                textTransform='uppercase'
                letterSpacing='0.1em'>
                Signos Vitales Actuales
              </Text>
            </Flex>
            <SimpleGrid columns={3} gap='8px'>
              {svItems.map(({ label, value, mono }) => (
                <Flex key={label} direction='column' gap='1px'>
                  <Text
                    fontSize='9px'
                    fontWeight='700'
                    color={mutedColor}
                    textTransform='uppercase'
                    letterSpacing='0.07em'>
                    {label}
                  </Text>
                  <Text
                    fontSize='sm'
                    fontWeight='700'
                    color={textColor}
                    fontFamily={mono ? 'mono' : undefined}>
                    {value}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </Box>
        </>
      )}
    </Card>
  );
}
